import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Card, Chip, Container, Divider, Grid, Stack, SwipeableDrawer, TextField, Typography, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import PermissionRequired from '../../../errorPages/permissionRequired';
import { accessVerify, capitalize, safeJSONParse, utcMoment } from '../../../../utils/common';
import { getUsers } from '../../../../redux/slices/hrRedux/UserManagementRedux/userManagementRedux';
import { getStockOrders, updateOrderStatus } from '../../../../redux/slices/stockManagementRedux/stockOrderRedux';
import { getStocks } from '../../../../redux/slices/stockManagementRedux/stockRedux';
import { addStockTransferSender, notifyBossAboutStockTransferMistake } from '../../../../redux/slices/stockManagementRedux/stockTransferRedux';
import { toast } from 'react-toastify';
import useSound from 'use-sound';
import successFx from '../../../../assets/sounds/success.wav';
import errorFx from '../../../../assets/sounds/error.wav';

const ViewAndSendStockOrders = () => {
  const { themeStretch } = useSettings();
  const [ordersData, setOrdersData] = useState([]);
  const [rowSelection, setRowSelection] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [itemsState, setItemsState] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [responsible, setResponsible] = useState({ name: '', phoneNumber: '94', note: '' });
  const [pendingScans, setPendingScans] = useState([]);
  const [pendingStockIdList, setPendingStockIdList] = useState([]);
  const [selectedCrate, setSelectedCrate] = useState(new Set());
  const [cratesHistory, setCratesHistory] = useState([]);
  const [awaitingTransfer, setAwaitingTransfer] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState(null);
  const [baselineTransferId, setBaselineTransferId] = useState(null);
  const [hydrateQueue, setHydrateQueue] = useState([]);
  const [hydrationActive, setHydrationActive] = useState(false);
  const hydratedRef = useRef(new Set());
  const lastAppliedInsertIdRef = useRef(null);

  const { data: users } = useSelector((s) => s.user);
  const { data: locations, selectedLocation } = useSelector((s) => s.location);
  const { data: orderList, addData: orderUpdateResult } = useSelector((s) => s.stockOrder);
  const { data: stocks } = useSelector((s) => s.stock);
  const { addData: transferResult } = useSelector((s) => s.stockTransfer);

  const [playErrorSound] = useSound(errorFx, { volume: 0.8, playbackRate: 1.2 });
  const [playSuccessSound] = useSound(successFx, { volume: 0.8, playbackRate: 1.2 });

  const parseQty = (s) => {
    const str = String(s ?? '').trim();
    const m = str.match(/([\d.]+)/);
    const unit = str.replace(/[\d.\s]/g, '').trim();
    return { qty: m ? parseFloat(m[1]) : Number(str) || 0, unit, raw: str };
  };

  const getInsertId = (obj) => obj?.data?.insertId || obj?.data?.insertedId || obj?.insertId || obj?.insertedId || null;

  useEffect(() => {
    dispatch(getUsers());
  }, []);

  useEffect(() => {
    if (selectedLocation?.id) dispatch(getStockOrders(selectedLocation?.id));
  }, [selectedLocation?.id]);

  useEffect(() => {
    setOrdersData(orderList);
  }, [orderList]);

  useEffect(() => {
    if (orderUpdateResult?.data) {
      if (selectedLocation?.id) dispatch(getStockOrders(selectedLocation?.id));
      setDrawerOpen(false);
    }
  }, [orderUpdateResult, selectedLocation?.id]);

  useEffect(() => {
    const currentInsert = getInsertId(transferResult);
    if (awaitingTransfer && currentInsert && String(currentInsert) !== String(baselineTransferId) && String(currentInsert) !== String(lastAppliedInsertIdRef.current || '')) {
      lastAppliedInsertIdRef.current = currentInsert;
      const submitted = lastSubmitted?.barcodes || [];
      setPendingScans((prev) => prev.filter((b) => !submitted.includes(b.barcode)));
      setPendingStockIdList((prev) => prev.filter((s) => !submitted.includes(s.barcode)));
      setItemsState((prev) =>
        prev.map((it) => {
          const nextScanned = it.scannedBarcodes.map((x) => {
            if (submitted.includes(x.barcode)) {
              return { ...x, status: 'sent', transferId: currentInsert };
            }
            return x;
          });
          return { ...it, scannedBarcodes: nextScanned };
        })
      );
      setSelectedCrate(new Set());
      setCratesHistory((prev) =>
        prev.concat([
          {
            id: prev.length + 1,
            transferId: currentInsert,
            count: submitted.length,
            type: 'crate',
            at: new Date().toISOString(),
            status: '0',
            name: responsible.name,
            contact: responsible.phoneNumber,
            note: responsible.note
          }
        ])
      );
      setBaselineTransferId(currentInsert);
      setAwaitingTransfer(false);
      setLastSubmitted(null);
    }
  }, [transferResult, awaitingTransfer, baselineTransferId, lastSubmitted, responsible]);

  const openOrder = (row) => {
    const parsed = safeJSONParse(row?.original?.stockorders_food_details);
    const items = (parsed || [])
      .sort((a, b) => {
        const priorityA = a?.foodDetails?.priority || 0;
        const priorityB = b?.foodDetails?.priority || 0;
        return priorityA - priorityB;
      })
      .map((it, idx) => {
        const fd = it?.foodDetails || {};
        const rq = parseQty(it?.requiredQty);
        return {
          idx,
          foodId: fd?.id || it?.foodId || null,
          code: fd?.code || '',
          name: fd?.name || '',
          description: fd?.description || '',
          requiredQtyNum: rq.qty,
          requiredQtyStr: rq.raw,
          requiredUnit: rq.unit,
          collectedQty: 0,
          scannedBarcodes: []
        };
      });

    const existingTransfers = safeJSONParse(row?.original?.transfers) || [];
    const normalizedTransfers = existingTransfers.map((t, i) => ({
      id: i + 1,
      transferId: t?.id,
      count: Array.isArray(t?.items) ? t.items.length : 0,
      type: 'crate',
      at: t?.dateTime ? new Date(t.dateTime).toISOString() : null,
      status: String(t?.status ?? ''),
      name: t?.name || '',
      contact: t?.contact || '',
      note: t?.note || ''
    }));

    const queue = [];
    existingTransfers.forEach((t) => {
      (t?.items || []).forEach((it) => {
        queue.push({ barcode: it?.code, qty: Number(it?.qty || 0), transferId: t?.id });
      });
    });
    const codes = Array.from(new Set(queue.map((q) => q.barcode).filter(Boolean)));

    hydratedRef.current = new Set();
    setSelectedOrder({ ...row.original, stockorders_food_details: parsed, transfers: existingTransfers });
    setItemsState(items);
    setPendingScans([]);
    setPendingStockIdList([]);
    setSelectedCrate(new Set());
    setCratesHistory(normalizedTransfers);
    setBarcodeInput('');
    setResponsible({ name: '', phoneNumber: '94', note: '' });
    setBaselineTransferId(getInsertId(transferResult));
    lastAppliedInsertIdRef.current = getInsertId(transferResult);
    setHydrateQueue(queue);
    setHydrationActive(codes.length > 0);
    if (codes.length > 0) dispatch(getStocks(-1, -1, -1, codes));
    setDrawerOpen(true);
  };

  useEffect(() => {
    if (!hydrationActive) return;
    if (!Array.isArray(stocks) || stocks.length === 0) return;
    const queueByCode = new Map(hydrateQueue.map((h) => [h.barcode, h]));
    const toProcess = hydrateQueue.filter((h) => !hydratedRef.current.has(h.barcode));
    if (toProcess.length === 0) {
      setHydrationActive(false);
      return;
    }
    const foundCodes = new Set();
    setItemsState((prev) => {
      let state = prev;
      stocks.forEach((s) => {
        const code = s?.stockCode;
        if (!code) return;
        if (hydratedRef.current.has(code)) return;
        const plan = queueByCode.get(code);
        if (!plan) return;
        const fd = safeJSONParse(s?.foodInfo) || {};
        const unitInfo = safeJSONParse(s?.unitInfo) || {};
        const foodIdFromStock = fd?.foodId || fd?.id || null;
        const unitSym = unitInfo?.unitSymbol || unitInfo?.unitName || '';
        const idx = state.findIndex((it) => String(it.foodId) === String(foodIdFromStock));
        if (idx === -1) return;
        const next = [...state];
        const item = next[idx];
        next[idx] = {
          ...item,
          collectedQty: Number(item.collectedQty || 0) + Number(plan.qty || 0),
          scannedBarcodes: item.scannedBarcodes.concat([{ barcode: plan.barcode, qty: Number(plan.qty || 0), unit: unitSym, status: 'sent', transferId: plan.transferId }])
        };
        state = next;
        hydratedRef.current.add(code);
        foundCodes.add(code);
      });
      return state;
    });
    if (foundCodes.size > 0) {
      const nextQueue = hydrateQueue.filter((h) => !foundCodes.has(h.barcode));
      setHydrateQueue(nextQueue);
      if (nextQueue.length === 0) setHydrationActive(false);
    }
  }, [stocks, hydrationActive, hydrateQueue]);

  const orderColumns = useMemo(() => {
    const sample = ordersData?.[0];
    if (!sample) return [];
    return Object.keys(sample)
      .map((key) => {
        if (['transferAddedUserId', 'stockorders_food_details', 'stockDetailsArray', 'transfers'].includes(key)) return null;
        if (key === 'transferStatus') {
          return {
            accessorKey: key,
            header: capitalize(key),
            Cell: ({ row }) => {
              if (row?.original?.transferStatus === '0') return <Chip label="Pending" color="warning" />;
              if (row?.original?.transferStatus === '1') return <Chip label="Done" color="success" />;
              return '-';
            }
          };
        }
        if (key === 'dateTime') return { accessorKey: key, header: capitalize(key), Cell: ({ row }) => utcMoment(row?.original?.dateTime).format('YYYY-MM-DD') };
        if (key === 'time') return { accessorKey: key, header: capitalize(key), Cell: ({ row }) => utcMoment(row?.original?.dateTime).format('HH:mm:ss') };
        if (key === 'requiredDate') return { accessorKey: key, header: capitalize(key), Cell: ({ row }) => utcMoment(row?.original?.requiredDate).format('YYYY-MM-DD') };
        if (key === 'userId') return { accessorKey: key, header: 'Created By', Cell: ({ row }) => users?.find((u) => u.id === row?.original?.userId)?.name || '-' };
        if (key === 'status') {
          return {
            accessorKey: key,
            header: 'Status',
            Cell: ({ row }) => {
              const s = Number(row?.original?.status);
              const hasTransfers = (safeJSONParse(row?.original?.transfers) || []).length > 0;
              if (s === 1) return <Chip label="Completed" color="success" />;
              if (s === 2) return <Chip label="Processing" color="info" />;
              if (s === 0 && hasTransfers) return <Chip label="Processing" color="info" />;
              if (s === 0) return <Chip label="Pending" color="warning" />;
              return <Chip label={String(s)} />;
            }
          };
        }
        if (['fromLocation', 'toLocation'].includes(key)) return { accessorKey: key, header: capitalize(key), Cell: ({ row }) => locations?.find((l) => l.id === row?.original?.[key])?.name || '-' };
        return { accessorKey: key, header: capitalize(key) };
      })
      .filter(Boolean);
  }, [ordersData, users, locations]);

  const itemsColumns = useMemo(() => {
    return [
      { accessorKey: 'code', header: 'Food Code' },
      { accessorKey: 'name', header: 'Product Name' },
      { accessorKey: 'description', header: 'Description' },
      { accessorKey: 'requiredQtyStr', header: 'Required Qty' },
      {
        accessorKey: 'collectedQty',
        header: 'Collected',
        Cell: ({ row }) => {
          const collected = Number(row?.original?.collectedQty || 0);
          const req = Number(row?.original?.requiredQtyNum || 0);
          const under = collected < req;
          const over = collected > req;
          const color = under ? 'error' : over ? 'warning' : 'success';
          return <Chip label={`${collected}/${req} ${row?.original?.requiredUnit}`} color={color} />;
        }
      },
      {
        accessorKey: 'scannedBarcodes',
        header: 'Scanned',
        Cell: ({ row }) => {
          const list = row?.original?.scannedBarcodes || [];
          const pending = list.filter((b) => b.status !== 'sent');
          const sent = list.filter((b) => b.status === 'sent');
          return (
            <Stack spacing={0.5}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {pending.map((b) => {
                  const selected = selectedCrate.has(b.barcode);
                  return (
                    <Chip
                      key={`p-${b.barcode}`}
                      label={`${b.barcode}${b.qty ? ` (${b.qty}${b.unit ? ` ${b.unit}` : ''})` : ''}`}
                      variant={selected ? 'filled' : 'outlined'}
                      color={selected ? 'info' : 'default'}
                      onClick={() => toggleCrateSelect(b.barcode)}
                      onDelete={() => removeScanned(b.barcode, true)}
                    />
                  );
                })}
              </Box>
              {sent.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                  {sent.map((b) => (
                    <Chip key={`s-${b.barcode}`} label={`${b.barcode}${b.qty ? ` (${b.qty}${b.unit ? ` ${b.unit}` : ''})` : ''}${b.transferId ? ` • T#${b.transferId}` : ''}`} color="success" variant="outlined" />
                  ))}
                </Box>
              )}
            </Stack>
          );
        }
      }
    ];
  }, [itemsState, selectedCrate]);

  const toggleCrateSelect = (barcode) => {
    setSelectedCrate((prev) => {
      const next = new Set(prev);
      if (next.has(barcode)) next.delete(barcode);
      else next.add(barcode);
      return next;
    });
  };

  const removeScanned = (barcode, adjustCollected) => {
    const found = pendingStockIdList.find((s) => s.barcode === barcode);
    const qty = found?.qty || found?.currentQty || 0;
    setPendingScans((prev) => prev.filter((b) => b.barcode !== barcode));
    setPendingStockIdList((prev) => prev.filter((s) => s.barcode !== barcode));
    setSelectedCrate((prev) => {
      const next = new Set(prev);
      next.delete(barcode);
      return next;
    });
    setItemsState((prev) =>
      prev.map((it) => {
        if (it.scannedBarcodes.some((x) => x.barcode === barcode && x.status !== 'sent')) {
          const updated = it.scannedBarcodes.filter((x) => x.barcode !== barcode || x.status === 'sent');
          return { ...it, scannedBarcodes: updated, collectedQty: adjustCollected ? Math.max(0, Number(it.collectedQty || 0) - Number(qty || 0)) : it.collectedQty };
        }
        return it;
      })
    );
  };

  const fetchAndValidateBarcode = (barcode) => {
    if (!barcode) return;
    if (pendingScans.some((b) => b.barcode === barcode)) {
      toast.error('Already scanned');
      return;
    }
    dispatch(getStocks(-1, -1, -1, [barcode]));
  };

  useEffect(() => {
    if (!stocks || stocks.length === 0) return;
    if (!barcodeInput) return;
    const stockInfo = stocks.find((s) => s?.stockCode === barcodeInput);
    if (!stockInfo) {
      playErrorSound();
      toast.error('Invalid barcode');
      return;
    }
    const locInfo = safeJSONParse(stockInfo?.locationInfo);
    const sameLocation = locInfo?.locationId === selectedLocation?.id;
    const fd = safeJSONParse(stockInfo?.foodInfo) || {};
    const unitInfo = safeJSONParse(stockInfo?.unitInfo) || {};
    const foodIdFromStock = fd?.foodId || fd?.id || null;
    const eligible = stockInfo?.stockStatus === 1 && sameLocation && !!foodIdFromStock;
    if (!eligible) {
      playErrorSound();
      toast.error('Not eligible for transfer');
      return;
    }
    const targetIdx = itemsState.findIndex((it) => String(it.foodId) === String(foodIdFromStock));
    if (targetIdx === -1) {
      playErrorSound();
      toast.error('Scanned item not in this order');
      return;
    }
    const qtyToAdd = Number(stockInfo?.currentQty || 0);
    const unitSym = unitInfo?.unitSymbol || unitInfo?.unitName || '';
    setItemsState((prev) =>
      prev.map((it, i) => {
        if (i !== targetIdx) return it;
        return {
          ...it,
          collectedQty: Number(it.collectedQty || 0) + qtyToAdd,
          scannedBarcodes: it.scannedBarcodes.concat([{ barcode: barcodeInput, qty: qtyToAdd, unit: unitSym, status: 'pending', transferId: null }])
        };
      })
    );
    setPendingScans((prev) => prev.concat([{ barcode: barcodeInput, qty: qtyToAdd, unit: unitSym }]));
    setPendingStockIdList((prev) =>
      prev.concat([{ barcode: barcodeInput, stockId: stockInfo?.stockId, currentQty: stockInfo?.currentQty, qty: qtyToAdd, unit: unitSym, costPerUnit: stockInfo?.costPerUnit }])
    );
    setBarcodeInput('');
    playSuccessSound();
  }, [stocks]);

  const handleScanEnter = (e) => {
    if (e.key === 'Enter') {
      const code = String(e.target.value || '').trim();
      if (!code) return;
      e.target.select();
      setBarcodeInput(code);
      fetchAndValidateBarcode(code);
    }
  };

  const buildMissingItemsMessage = () => {
    const underfilled = itemsState.filter((i) => Number(i.collectedQty) < Number(i.requiredQtyNum));
    if (underfilled.length === 0) return '';
    const list = underfilled
      .map((i) => {
        const req = Number(i.requiredQtyNum) || 0;
        const col = Number(i.collectedQty) || 0;
        const miss = Math.max(0, req - col);
        const unit = i.requiredUnit ? ` ${i.requiredUnit}` : '';
        const label = i.name || i.code || 'Item';
        return `- ${label}: need ${req}${unit}, collected ${col}${unit} (missing ${miss}${unit})`;
      })
      .join('\n');
    return `Some items are below the required quantities:\n${list}`;
  };

  const buildMissingItemsMessageForBoss = () => {
    const underfilled = itemsState.filter((i) => Number(i.collectedQty) < Number(i.requiredQtyNum));
    if (underfilled.length === 0) return '';
    const list = underfilled
      .map((i) => {
        const req = Number(i.requiredQtyNum) || 0;
        const col = Number(i.collectedQty) || 0;
        const miss = Math.max(0, req - col);
        const unit = i.requiredUnit ? ` ${i.requiredUnit}` : '';
        const label = i.name || i.code || 'Item';
        return `- ${label}: ${miss}${unit}`;
      })
      .join('\n');
    return ` ${list}`;
  };

  const initiateCrate = () => {
    if (!selectedOrder) return;
    if (!responsible.name || String(responsible.name).trim().length === 0) {
      toast.error('Responsible person name is required');
      return;
    }
    if (String(responsible.phoneNumber || '').length !== 11) {
      toast.error('Please enter valid phone number');
      return;
    }
    const crateBarcodes = Array.from(selectedCrate);
    if (crateBarcodes.length === 0) {
      toast.error('Select at least one barcode for the crate');
      return;
    }
    const crateStockList = pendingStockIdList.filter((s) => crateBarcodes.includes(s.barcode));
    const payload = {
      stockOrderId: selectedOrder.id,
      name: responsible.name,
      phoneNumber: responsible.phoneNumber,
      note: responsible.note,
      barcode: crateBarcodes,
      stockIdList: crateStockList,
      fromLocationId: selectedOrder.fromLocation,
      toLocationId: selectedOrder?.toLocation,
    };
    setAwaitingTransfer(true);
    setLastSubmitted({ barcodes: crateBarcodes, type: 'crate' });
    dispatch(addStockTransferSender(payload));
  };

  const initiateFinalAndComplete = () => {
    if (!selectedOrder) return;
    if (cratesHistory.length === 0) {
      toast.error('Create at least one crate to complete the order');
      return;
    }
    if (pendingScans.length > 0) {
      toast.error('You have uncrated scanned items. Create a crate for them before completing');
      return;
    }
    const msg = buildMissingItemsMessage();
    if (msg) {
      const confirmClose = window.confirm(`${msg}\n\nDo you want to proceed and mark this order as completed?`);
      if (!confirmClose) return;
      dispatch(
        notifyBossAboutStockTransferMistake({
          code: '',
          locationId: selectedOrder?.toLocation,
          tag: 'missingItemsOnStockOrder',
          message: `[Stock Order Missing Items For ${locations?.find((l) => l.id === selectedOrder?.toLocation)?.name}]\n\n${buildMissingItemsMessageForBoss()}`
        })
      );
    }
    if (selectedOrder?.id) dispatch(updateOrderStatus({ id: selectedOrder.id, status: 1 }));
  };

  const tryCloseDrawer = () => {
    const msg = buildMissingItemsMessage();
    if (msg) {
      const ok = window.confirm(`${msg}\n\nAre you sure you want to close without completing?`);
      if (!ok) return;
    }
    setDrawerOpen(false);
  };

  const fulfilledCount = useMemo(() => itemsState.filter((i) => Number(i.collectedQty) >= Number(i.requiredQtyNum)).length, [itemsState]);

  const statusChip = (s) => {
    if (String(s) === '1') return <Chip size="small" label="Confirmed" color="success" />;
    if (String(s) === '0') return <Chip size="small" label="Pending" color="warning" />;
    if (String(s) === '2') return <Chip size="small" label="Processing" color="info" />;
    return <Chip size="small" label="Unknown" color="default" />;
  };

  return (
    <Page title="View & Send Stock Orders">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs heading="View & Send Stock Orders" links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Stock', href: '' }, { name: 'Orders' }]} action={[]} />
        {accessVerify('STOCK_ORDER_SENDER_VIEW') ? (
          <DataGridTable
            name="View Stock Orders"
            data={ordersData}
            column={orderColumns}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            isRowClickable
            keyField="id"
            enableRowActions
            onRowClick={(row) => openOrder(row)}
            renderRowActionItems={() => []}
          />
        ) : (
          <PermissionRequired />
        )}
        <SwipeableDrawer
          anchor="top"
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={tryCloseDrawer}
          PaperProps={{ sx: { width: '94%', height: '92%', ml: '3%', mt: '2%', borderRadius: 3, overflow: 'auto' } }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
            <Grid container alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h6">Order #{selectedOrder?.id}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {locations?.find((l) => l.id === selectedOrder?.fromLocation)?.name} → {locations?.find((l) => l.id === selectedOrder?.toLocation)?.name} • Required {selectedOrder?.requiredDate ? utcMoment(selectedOrder?.requiredDate).format('YYYY-MM-DD') : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Chip label={`${fulfilledCount}/${itemsState.length} Items Met`} color={fulfilledCount === itemsState.length ? 'success' : 'default'} />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField type="text" label="Scan Barcode" fullWidth onKeyDown={handleScanEnter} />
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <DataGridTable
                    name="Required Items"
                    data={itemsState}
                    column={itemsColumns}
                    isLoading={false}
                    rowSelection={false}
                    setRowSelection={() => {}}
                    isRowClickable={false}
                    keyField="idx"
                    enableRowActions={false}
                    maxHeight="52vh"
                    rowsPerPage={8}
                    getRowProps={(row) => {
                      const collected = Number(row.original.collectedQty);
                      const req = Number(row.original.requiredQtyNum);
                      const under = collected < req;
                      const over = collected > req;
                      const met = collected === req;
                      const bg = under ? 'rgba(211, 47, 47, 0.18)' : over ? 'rgba(255, 193, 7, 0.18)' : met ? 'rgba(46, 125, 50, 0.18)' : undefined;
                      return { style: { backgroundColor: bg } };
                    }}
                  />
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={3}>
                  <Card sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Responsible Person</Typography>
                    <TextField label="Name" value={responsible.name} onChange={(e) => setResponsible({ ...responsible, name: e.target.value })} />
                    <TextField label="Contact Number" placeholder="Enter phone (+94)" sx={{ mt: 2 }} value={responsible.phoneNumber} onChange={(e) => setResponsible({ ...responsible, phoneNumber: e.target.value })} />
                    <TextField label="Note" placeholder="Address / Vehicle Number etc" sx={{ mt: 2 }} value={responsible.note} onChange={(e) => setResponsible({ ...responsible, note: e.target.value })} />
                  </Card>
                  <Card sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Pending Scanned Barcodes</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, maxHeight: 180, overflow: 'auto', mb: 1 }}>
                      {pendingScans.map((b) => {
                        const selected = selectedCrate.has(b.barcode);
                        return (
                          <Chip
                            key={b.barcode}
                            label={`${b.barcode}${b.qty ? ` (${b.qty}${b.unit ? ` ${b.unit}` : ''})` : ''}`}
                            variant={selected ? 'filled' : 'outlined'}
                            color={selected ? 'info' : 'default'}
                            onClick={() => toggleCrateSelect(b.barcode)}
                            onDelete={() => removeScanned(b.barcode, true)}
                          />
                        );
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" onClick={() => setSelectedCrate(new Set(pendingScans.map((b) => b.barcode)))}>Select All</Button>
                      <Button size="small" variant="outlined" onClick={() => setSelectedCrate(new Set())}>Clear Selection</Button>
                    </Box>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      Selected {selectedCrate.size} / {pendingScans.length}
                    </Typography>
                  </Card>
                  <Card sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Crates History</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, maxHeight: 220, overflow: 'auto' }}>
                      {cratesHistory.length === 0 && <Typography variant="body2" sx={{ opacity: 0.7 }}>No crates or transfers yet</Typography>}
                      {cratesHistory.map((c) => (
                        <Box key={`crate-${c.id}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Stack spacing={0.3}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.type === 'final' ? 'Final' : 'Crate'} #{c.id} • Transfer #{c.transferId || '-'}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              {c.at ? utcMoment(c.at).format('YYYY-MM-DD HH:mm') : '-'} • {c.count} item(s)
                            </Typography>
                            {(c.name || c.contact) && (
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {c.name ? c.name : ''}{c.name && c.contact ? ' • ' : ''}{c.contact ? c.contact : ''}
                              </Typography>
                            )}
                            {c.note ? <Typography variant="caption" sx={{ opacity: 0.8 }}>Note: {c.note}</Typography> : null}
                          </Stack>
                          {statusChip(c.status)}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                  <LoadingButton variant="contained" size="large" disabled={selectedCrate.size === 0} onClick={initiateCrate}>
                    Initiate Selected Crate
                  </LoadingButton>
                  <LoadingButton variant="outlined" size="large" disabled={cratesHistory.length === 0 || pendingScans.length > 0} onClick={initiateFinalAndComplete}>
                    Complete Order Now
                  </LoadingButton>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </SwipeableDrawer>
      </Container>
    </Page>
  );
};

export default ViewAndSendStockOrders;
